import React, { useState, useEffect } from 'react';
import {CreateConsentResponse} from '../blinkpay';
import {createConsent} from '../demoBffClientInstance';

interface DecoupledFlowProps {
    onSubmit?: (payload: any) => void;
    onClose?: () => void;
}

const DecoupledFlow: React.FC<DecoupledFlowProps> = ({ onSubmit, onClose }) => {
    const isProd = process.env.REACT_APP_ENV === 'prod';
    const [bank, setBank] = useState<string>(isProd ? 'ANZ' : 'PNZ');
    const [callbackUrl, setCallbackUrl] = useState<string>('https://www.blinkpay.co.nz/callback');
    const [identifierType, setIdentifierType] = useState<string>('mobile_number');
    const [identifierValue, setIdentifierValue] = useState<string>('0259531933');
    const [permissions, setPermissions] = useState({
        ReadAccounts: true,
        ReadBalances: true,
        ReadTransactions: true,
        ReadStatements: true,
        ReadParty: true
    });
    const [expirationDateTime, setExpirationDateTime] = useState<string>('');
    const [startDateTime, setStartDateTime] = useState<string>('');
    const [endDateTime, setEndDateTime] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const banks = [
        ...(isProd ? [] : [{ id: 'PNZ', name: 'PNZ' }]),
        { id: 'ANZ', name: 'ANZ' },
        { id: 'ASB', name: 'ASB' },
        // { id: 'BNZ', name: 'BNZ' },
        { id: 'Westpac', name: 'Westpac' }
    ];

    const getIdentifierTypesForBank = (bankId: string) => {
        switch (bankId) {
            case 'PNZ':
                return [{ id: 'mobile_number', name: 'Mobile Number' }];
            case 'ANZ':
                return [{ id: 'mobile_number', name: 'Mobile Number' }];
            case 'ASB':
                return [
                    { id: 'mobile_number', name: 'Mobile Number' },
                    { id: 'consent_id', name: 'Consent ID' }
                ];
            case 'Westpac':
                return [
                    { id: 'banking_username', name: 'Customer ID' },
                    { id: 'consent_id', name: 'Consent ID' }
                ];
            default:
                return [
                    { id: 'mobile_number', name: 'Mobile Number' },
                    { id: 'banking_username', name: 'Customer ID' },
                    { id: 'consent_id', name: 'Consent ID' }
                ];
        }
    };

    const identifierTypes = getIdentifierTypesForBank(bank);

    useEffect(() => {
        // When bank changes, reset identifier type to the first available option
        const availableTypes = getIdentifierTypesForBank(bank);
        const isCurrentTypeValid = availableTypes.some(type => type.id === identifierType);

        if (!isCurrentTypeValid) {
            setIdentifierType(availableTypes[0].id);
        }
    }, [bank, identifierType]);

    useEffect(() => {
        // Update identifier value based on bank and identifier type
        if (bank === 'PNZ' && identifierType === 'mobile_number') {
            setIdentifierValue('0259531933');
        } else if (bank === 'ANZ' && identifierType === 'mobile_number') {
            setIdentifierValue('021835993');
        } else if (bank === 'ASB' && identifierType === 'mobile_number') {
            setIdentifierValue('0270274741033');
        } else if (bank === 'ASB' && identifierType === 'consent_id') {
            setIdentifierValue('');
        } else if (bank === 'Westpac' && identifierType === 'banking_username') {
            setIdentifierValue('347341314');
        } else if (bank === 'Westpac' && identifierType === 'consent_id') {
            setIdentifierValue('');
        }
    }, [bank, identifierType]);

    const handlePermissionChange = (permission: string) => {
        setPermissions(prev => ({
            ...prev,
            [permission]: !prev[permission as keyof typeof prev]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const selectedPermissions = Object.entries(permissions)
            .filter(([_, selected]) => selected)
            .map(([permission]) => permission);

        const payload: any = {
            flow: {
                detail: {
                    type: 'decoupled',
                    bank: bank,
                    callback_url: callbackUrl,
                    identifier_type: identifierType,
                    identifier_value: identifierValue
                }
            },
            permissions: selectedPermissions
        };

        if (expirationDateTime) {
            payload.expiration_date_time = new Date(expirationDateTime).toISOString();
        }
        if (startDateTime) {
            payload.start_date_time = new Date(startDateTime).toISOString();
        }
        if (endDateTime) {
            payload.end_date_time = new Date(endDateTime).toISOString();
        }

        console.log('Decoupled Flow Payload:', payload);

        try {
            // Call the Blink API to create consent
            console.log('Creating consent...');

            const response: CreateConsentResponse = await createConsent(payload);
            console.log('Consent created successfully:', response);

            if (response?.consentId) {
                console.log('Consent ID:', response.consentId);

                // Store consent ID in localStorage for demo purposes
                localStorage.setItem('consentId', response.consentId);

                // For decoupled flow, show instructions
                alert(`Consent created! Please complete authentication on your registered device.\nConsent ID: ${response.consentId}`);

                // Navigate to redirect page with consent ID after a delay
                setTimeout(() => {
                    window.location.href = `/redirect?cid=${response.consentId}`;
                }, 3000);
            } else {
                setError('No consent ID received from server');
            }

            if (onSubmit) {
                onSubmit(response);
            }
        } catch (error: any) {
            console.error('Error creating consent:', error);
            console.error('Error details:', {
                message: error?.message,
                response: error?.response,
                stack: error?.stack
            });

            // Check various error types
            let errorMessage = 'Failed to create consent';
            if (error?.message?.includes('Failed to refresh access token')) {
                errorMessage = 'Failed to authenticate with Blink API. Please check your client credentials.';
            } else if (error?.message?.includes('Network Error') || error?.code === 'ECONNREFUSED') {
                errorMessage = 'Cannot connect to Blink API. Please check your REACT_APP_BLINKPAY_API_GATEWAY_URL configuration.';
            } else if (error?.response?.status === 401) {
                errorMessage = 'Authentication failed. Please check your API credentials.';
            } else if (error?.response?.status === 400) {
                errorMessage = 'Invalid request: ' + (error?.response?.data?.message || 'Please check the request payload.');
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const getIdentifierPlaceholder = () => {
        switch (identifierType) {
            case 'phone_number':
                return '+64-XXXXXXXXX';
            case 'email':
                return 'user@example.com';
            case 'consent_id':
                return 'consent-id-here';
            default:
                return 'Enter identifier value';
        }
    };

    const getIdentifierPattern = () => {
        switch (identifierType) {
            case 'phone_number':
                return '^\\+?[0-9\\-\\s]+$';
            case 'email':
                return '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';
            default:
                return '.*';
        }
    };

    return (
        <div className="decoupled-flow-modal">
            <div className="decoupled-flow-container">
                <div className="decoupled-flow-header">
                    <h2>Decoupled Flow Configuration</h2>
                    {onClose && (
                        <button className="close-btn" onClick={onClose}>×</button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="decoupled-flow-form">
                    <div className="form-section">
                        <h3>Bank Selection</h3>
                        <div className="bank-selection">
                            {banks.map((b) => (
                                <label key={b.id} className={`bank-option ${bank === b.id ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="bank"
                                        value={b.id}
                                        checked={bank === b.id}
                                        onChange={(e) => setBank(e.target.value)}
                                    />
                                    <span>{b.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Callback Configuration</h3>
                        <div className="form-group">
                            <label htmlFor="callback-url">Callback URL:</label>
                            <input
                                id="callback-url"
                                type="url"
                                value={callbackUrl}
                                onChange={(e) => setCallbackUrl(e.target.value)}
                                required
                                className="form-input"
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Identifier Configuration</h3>
                        <div className="identifier-grid">
                            <div className="form-group">
                                <label htmlFor="identifier-type">Identifier Type:</label>
                                <select
                                    id="identifier-type"
                                    value={identifierType}
                                    onChange={(e) => setIdentifierType(e.target.value)}
                                    className="form-input"
                                >
                                    {identifierTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="identifier-value">Identifier Value:</label>
                                <input
                                    id="identifier-value"
                                    type="text"
                                    value={identifierValue}
                                    onChange={(e) => setIdentifierValue(e.target.value)}
                                    pattern={getIdentifierPattern()}
                                    placeholder={getIdentifierPlaceholder()}
                                    required
                                    className="form-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Permissions</h3>
                        <div className="permissions-grid">
                            {Object.entries(permissions).map(([permission, checked]) => (
                                <label key={permission} className="permission-option">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handlePermissionChange(permission)}
                                    />
                                    <span>{permission.replace(/([A-Z])/g, ' $1').trim()}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Date/Time Configuration (Optional)</h3>
                        <div className="datetime-grid">
                            <div className="form-group">
                                <label htmlFor="start-datetime">Start Date/Time:</label>
                                <input
                                    id="start-datetime"
                                    type="datetime-local"
                                    value={startDateTime}
                                    onChange={(e) => setStartDateTime(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="end-datetime">End Date/Time:</label>
                                <input
                                    id="end-datetime"
                                    type="datetime-local"
                                    value={endDateTime}
                                    onChange={(e) => setEndDateTime(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="expiration-datetime">Expiration Date/Time (Null means indefinite):</label>
                                <input
                                        id="expiration-datetime"
                                        type="datetime-local"
                                        value={expirationDateTime}
                                        onChange={(e) => setExpirationDateTime(e.target.value)}
                                        className="form-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? 'Creating Consent...' : 'Initiate Decoupled Flow'}
                        </button>
                        {onClose && (
                            <button type="button" onClick={onClose} className="cancel-btn" disabled={loading}>
                                Cancel
                            </button>
                        )}
                    </div>

                    {error && (
                            <div className="error-message">
                                {error}
                            </div>
                    )}
                </form>
            </div>

            <style>{`
                .decoupled-flow-modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    overflow-y: auto;
                }

                .decoupled-flow-container {
                    background: white;
                    border-radius: 20px;
                    max-width: 800px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }

                .decoupled-flow-header {
                    padding: 30px;
                    border-bottom: 2px solid #f0f0f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .decoupled-flow-header h2 {
                    margin: 0;
                    color: #333;
                    font-size: 24px;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 30px;
                    color: #999;
                    cursor: pointer;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: all 0.3s ease;
                }

                .close-btn:hover {
                    background: #f0f0f0;
                    color: #333;
                }

                .decoupled-flow-form {
                    padding: 30px;
                }

                .form-section {
                    margin-bottom: 30px;
                }

                .form-section h3 {
                    color: #555;
                    font-size: 18px;
                    margin-bottom: 15px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #e0e0e0;
                }

                .bank-selection {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                    gap: 10px;
                }

                .bank-option {
                    display: flex;
                    align-items: center;
                    padding: 12px;
                    border: 2px solid #e0e0e0;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: white;
                }

                .bank-option:hover {
                    border-color: #f093fb;
                    background: #fdf4ff;
                }

                .bank-option.selected {
                    border-color: #f093fb;
                    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                    color: white;
                }

                .bank-option input[type="radio"] {
                    margin-right: 8px;
                    accent-color: #f093fb;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    color: #555;
                    font-weight: 500;
                    margin-bottom: 8px;
                }

                .form-input {
                    width: 100%;
                    padding: 12px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 16px;
                    background: #f8f9fa;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #f093fb;
                    background: white;
                }

                .identifier-grid {
                    display: grid;
                    grid-template-columns: 1fr 2fr;
                    gap: 20px;
                }

                .permissions-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 15px;
                }

                .permission-option {
                    display: flex;
                    align-items: center;
                    padding: 10px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .permission-option:hover {
                    background: #fdf4ff;
                    border-color: #f093fb;
                }

                .permission-option input[type="checkbox"] {
                    margin-right: 10px;
                    accent-color: #f093fb;
                    width: 18px;
                    height: 18px;
                }

                .datetime-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 20px;
                }

                .form-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: flex-end;
                    padding-top: 20px;
                    border-top: 1px solid #e0e0e0;
                }

                .submit-btn {
                    padding: 15px 30px;
                    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 20px rgba(240, 147, 251, 0.4);
                }

                .cancel-btn {
                    padding: 15px 30px;
                    background: #f0f0f0;
                    color: #666;
                    border: none;
                    border-radius: 10px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .submit-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .error-message {
                    background: #f8d7da;
                    border: 1px solid #f5c6cb;
                    color: #721c24;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .cancel-btn:hover {
                    background: #e0e0e0;
                    color: #333;
                }

                @media (max-width: 600px) {
                    .decoupled-flow-modal {
                        padding: 0;
                    }

                    .decoupled-flow-container {
                        border-radius: 0;
                        max-height: 100vh;
                    }

                    .bank-selection {
                        grid-template-columns: 1fr;
                    }

                    .identifier-grid {
                        grid-template-columns: 1fr;
                    }

                    .permissions-grid {
                        grid-template-columns: 1fr;
                    }

                    .datetime-grid {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .submit-btn,
                    .cancel-btn {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default DecoupledFlow;